import "./engine/serializationRegistry";
import "./ui/ui.css";
import { Game } from "./engine/game";
import { showMainMenu, TILESET_PATHS } from "./ui/mainMenu";
import { TilesetConfig } from "./ui/tilesetConfig/tilesetConfig";
import { allTilesetEntries } from "./ui/tilesetConfig/data/index";
import { AnimationLoader } from "./ui/animationLoader";
import { DrawableManager } from "./ui/drawableManager";
import { Camera } from "./ui/camera";
import { SpriteRenderer } from "./ui/spriteRenderer";
import { WEAPON_BAR_WIDTH } from "./ui/hud";
import { UserSettings } from "./ui/userSettings";
import { Language, Strings } from "./engine/strings";
import { allEnStrings, allPtBrStrings } from "./engine/strings/data";
import { SoundLoader } from "./ui/sound/soundLoader";
import { SoundManager } from "./ui/sound/soundManager";
import { initUiSounds, setUiSoundVolume } from "./ui/sound/uiSoundPlayer";
import { MusicPlayer } from "./ui/sound/musicPlayer";
import * as TileImageLoader from "./ui/tileImageLoader";
import { SAVE_KEY, createNewGame, createPlaygroundGame } from "./gameFactory";
import { parseStartupParams } from "./devParams";
import { getDevPanel } from "./ui/devPanel";
import { generateSeedString } from "./engine/random";
import { GameSessionImpl } from "./ui/gameSession";
import { initTelemetry, setOptOutGetter, trackSessionStart } from "./telemetry/telemetry";
import { trackRunStart } from "./telemetry/runPayloads";

let currentAssetsDir = "/assets-hd/";

const canvas = document.getElementById("game") as HTMLCanvasElement;
const ctx = canvas.getContext("2d")!;
const healthRow = document.getElementById("health-row")!;
healthRow.style.width = `${WEAPON_BAR_WIDTH}px`;

export interface GameSession {
	stop(): void;
}

async function launchGame(
	game: Game,
	drawableManager: DrawableManager,
	camera: Camera,
	spriteRenderer: SpriteRenderer,
	animationLoader: AnimationLoader,
	audioContext: AudioContext,
	soundManager: SoundManager,
	musicPlayer: MusicPlayer,
	userSettings: UserSettings,
	onQuitToMenu: (session: GameSession) => void,
): Promise<GameSession> {
	return GameSessionImpl.create({
		game,
		drawableManager,
		camera,
		spriteRenderer,
		animationLoader,
		audioContext,
		soundManager,
		musicPlayer,
		userSettings,
		onQuitToMenu,
		getCurrentAssetsDir: () => currentAssetsDir,
		setCurrentAssetsDir: (dir: string) => {
			currentAssetsDir = dir;
		},
	});
}

(async () => {
	initTelemetry();
	const userSettings = UserSettings.load();
	setOptOutGetter(() => userSettings.telemetryOptOut);
	const tilesetPaths = TILESET_PATHS[userSettings.tileset] ?? TILESET_PATHS.hd;
	currentAssetsDir = tilesetPaths.assetsDir;
	TileImageLoader.setAssetsDir(tilesetPaths.assetsDir);

	Strings.registerDictionary(Language.EN, allEnStrings);
	Strings.registerDictionary(Language.PT_BR, allPtBrStrings);

	const config = new TilesetConfig(allTilesetEntries);

	const animationLoader = new AnimationLoader(config, 20, 20);
	const drawableManager = new DrawableManager(animationLoader);
	const camera = new Camera(20, 20);
	const spriteRenderer = new SpriteRenderer(ctx, camera, drawableManager, animationLoader);

	// Load in background while the menu is shown.
	TileImageLoader.preloadAll();

	const audioContext = new AudioContext();
	const soundLoader = new SoundLoader(audioContext);
	const soundManager = new SoundManager(audioContext, soundLoader);
	const musicPlayer = new MusicPlayer(audioContext);
	// Load in background; if the player starts before sounds are ready, a loading screen shows.
	const soundPreloadPromise = soundLoader.preloadAll();
	initUiSounds(audioContext, soundLoader);
	soundManager.setVolumeLevel(userSettings.getEffectiveSoundVolume());
	setUiSoundVolume(userSettings.getEffectiveSoundVolume());
	musicPlayer.setVolume(userSettings.getEffectiveMusicVolume());
	Strings.setLanguage(userSettings.language);
	trackSessionStart(userSettings.language);

	const { startLevel, noMenu, playground, nodark, autoRestartIntervalMs } = parseStartupParams(window.location.search);

	document.getElementById("loading-screen")?.remove();
	getDevPanel();

	const menuOptions = {
		drawableManager,
		camera,
		spriteRenderer,
		animationLoader,
		audioContext,
		soundManager,
		musicPlayer,
		userSettings,
		startLevel,
		soundPreloadPromise,
		getCurrentAssetsDir: () => currentAssetsDir,
		setCurrentAssetsDir: (dir: string) => {
			currentAssetsDir = dir;
		},
		launchGame,
	};

	if (noMenu || playground) {
		function quitToMenu(session: GameSession): void {
			session.stop();
			showMainMenu(menuOptions);
		}

		let currentSession: GameSession | null = null;
		let autoRestartTimeoutId: ReturnType<typeof setTimeout> | null = null;
		let autoRestartCancelled = false;

		async function startFreshGame(): Promise<GameSession> {
			localStorage.removeItem(SAVE_KEY);
			let game;
			if (playground) {
				game = createPlaygroundGame(startLevel);
			} else if (autoRestartIntervalMs > 0) {
				// Fresh seed each cycle so each restart yields a different level.
				const seed = generateSeedString();
				console.log(`[auto_restart] new game seed=${seed}`);
				game = createNewGame(startLevel, seed);
			} else {
				game = createNewGame(startLevel);
			}
			if (nodark) {
				game.getCurrentBoard().darkness = 0;
			}
			trackRunStart(game);
			// Don't wait for sounds — launch immediately for fast iteration.
			return launchGame(
				game,
				drawableManager,
				camera,
				spriteRenderer,
				animationLoader,
				audioContext,
				soundManager,
				musicPlayer,
				userSettings,
				(session) => quitToMenu(session),
			);
		}

		function scheduleAutoRestart(): void {
			if (autoRestartCancelled || autoRestartIntervalMs <= 0) return;
			autoRestartTimeoutId = setTimeout(async () => {
				if (autoRestartCancelled) return;
				if (currentSession) currentSession.stop();
				currentSession = await startFreshGame();
				scheduleAutoRestart();
			}, autoRestartIntervalMs);
		}

		startFreshGame().then((session) => {
			currentSession = session;
			scheduleAutoRestart();
		});

		// ESC cancels the loop; the pause menu's own ESC handler also fires, so one keypress cycles → pause.
		if (autoRestartIntervalMs > 0) {
			const escHandler = (event: KeyboardEvent) => {
				if (event.key !== "Escape") return;
				if (autoRestartCancelled) return;
				autoRestartCancelled = true;
				if (autoRestartTimeoutId !== null) {
					clearTimeout(autoRestartTimeoutId);
					autoRestartTimeoutId = null;
				}
				console.log("[auto_restart] cancelled — staying on current level");
				window.removeEventListener("keydown", escHandler);
			};
			window.addEventListener("keydown", escHandler);
		}
	} else {
		showMainMenu(menuOptions);
	}
})();
